import apiClient from "../../services/apiClient";
import toast from "../../utils/toast";
import { useState, useEffect } from "react";
import { getCookie } from "../../utils/cookies";

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date)) return "-";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDiscount(coupon) {
  const isPercent = coupon.discountTypeText?.toLowerCase().includes("percent");
  return isPercent ? `${coupon.discountValue}%` : `₹${coupon.discountValue}`;
}

function formatAmount(amount) {
  const num = Number(amount) || 0;
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const GST_RATE = 0.18;

function evaluateCoupon(couponList, code, baseAmount) {
  const trimmedCode = code.trim();
  if (!trimmedCode) return null;

  const coupon = couponList.find(
    (c) => c.couponCode.toLowerCase() === trimmedCode.toLowerCase(),
  );

  if (!coupon) {
    return { valid: false, discount: 0, message: "Invalid coupon code" };
  }
  if (!coupon.isActive) {
    return { valid: false, discount: 0, message: "This coupon is not active" };
  }

  const now = new Date();
  if (coupon.validFrom && now < new Date(coupon.validFrom)) {
    return {
      valid: false,
      discount: 0,
      message: "This coupon is not valid yet",
    };
  }
  if (coupon.validTo && now > new Date(coupon.validTo)) {
    return { valid: false, discount: 0, message: "This coupon has expired" };
  }
  if (coupon.maxUsageCount > 0 && coupon.usedCount >= coupon.maxUsageCount) {
    return {
      valid: false,
      discount: 0,
      message: "This coupon has reached its usage limit",
    };
  }
  if (baseAmount < coupon.minOrderAmount) {
    return {
      valid: false,
      discount: 0,
      message: `Minimum order amount for this coupon is ${formatAmount(coupon.minOrderAmount)}`,
    };
  }

  let discount = 0;
  if (coupon.discountType === 1) {
    discount = (baseAmount * coupon.discountValue) / 100;
    if (coupon.maxDiscountAmount > 0) {
      discount = Math.min(discount, coupon.maxDiscountAmount);
    }
  } else {
    discount = coupon.discountValue;
  }
  discount = Math.min(discount, baseAmount);

  return {
    valid: true,
    discount,
    message: `Coupon applied! You saved ${formatAmount(discount)}`,
    coupon,
  };
}

const PAGE_SIZE = 10;

function Pagination({ currentPage, totalItems, pageSize, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  if (totalPages <= 1) return null;

  return (
    <div className="pagination-row">
      <button
        className="btn-sm"
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        Prev
      </button>
      <span className="pagination-info">
        Page {currentPage} of {totalPages}
      </span>
      <button
        className="btn-sm"
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
      >
        Next
      </button>
    </div>
  );
}

export default function ActivatePackage() {
  const [isCouponData, setCouponData] = useState();
  const [packageData, setPackageData] = useState();
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [isActivating, setIsActivating] = useState(false);
  const [packageHistoryData, setPackageHistoryData] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  const [historyPage, setHistoryPage] = useState(1);

  useEffect(() => {
    fetchCouponList();
    fetchPackageInformation();
    activatePackageHistory();
  }, []);

  const fetchCouponList = async () => {
    try {
      const userData = JSON.parse(getCookie("userData") || "{}");

      const res = await apiClient.post("/GetDiscountCouponListApp", {
        appid: "ROUNDPAYAPPID13APR20191351",
        imei: "d593bdd26dfbea56",
        loginTypeID: 1,
        serialNo: "d593bdd26dfbea56",
        session: getCookie("session") || "",
        sessionID: getCookie("sessionID") || "",
        uid: 0,
        userID: userData.userID || "",
        version: "1.0",
      });

      if (res.statuscode == 1) {
        setCouponData(res);
      }
    } catch (err) {
      console.log("error", err);
    }
  };

  function handleCopyCoupon(code) {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code);
      toast.success("Coupon copied!");
      return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = code;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");
      toast.success("Coupon copied!");
    } catch (err) {
      console.log("error", err);
      toast.error("Failed to copy coupon code");
    } finally {
      document.body.removeChild(textarea);
    }
  }

  const couponRows = isCouponData?.data || [];

  const fetchPackageInformation = async () => {
    try {
      const userData = JSON.parse(getCookie("userData") || "{}");

      const res = await apiClient.post("/GetTopupDetailsByUserID", {
        appSession: {
          appid: "ROUNDPAYAPPID13APR20191351",
          imei: "d593bdd26dfbea56",
          loginTypeID: "1",
          regKey: "",
          serialNo: "d593bdd26dfbea56",
          version: "1.0",
          uid: 0,
          session: getCookie("session") || "",
          sessionID: getCookie("sessionID") || "",
          userID: userData.userID || "",
        },
        Request: {
          BussinessEventID: 1,
          strUserID: userData.userID || "",
        },
      });

      if (res.statuscode == 1) {
        setPackageData(res.data || []);
      }
    } catch (err) {
      console.log("error", err);
    }
  };

  const topupInfo = packageData?.[0];
  const wallet = topupInfo?.walletTypeList?.[0];
  const packageOptions = topupInfo?.packageList || [];
  const packageInfo = packageOptions.find(
    (pkg) => String(pkg.packageId) === selectedPackageId,
  );

  const packageCost = packageInfo?.packageCost || 0;
  const couponResult = packageInfo
    ? evaluateCoupon(isCouponData?.data || [], couponCode, packageCost)
    : null;
  const couponDiscount = couponResult?.valid ? couponResult.discount : 0;
  const afterCouponAmount = Math.max(packageCost - couponDiscount, 0);
  const gstAmount = afterCouponAmount * GST_RATE;
  const totalPayable = afterCouponAmount + gstAmount;

  const submitActivatePackage = async () => {
    if (!packageInfo) {
      toast.error("Please select a package");
      return;
    }

    setIsActivating(true);
    try {
      const userData = JSON.parse(getCookie("userData") || "{}");

      const res = await apiClient.post("/ActivateUserByApp", {
        appSession: {
          appid: "ROUNDPAYAPPID13APR20191351",
          imei: "d593bdd26dfbea56",
          loginTypeID: "1",
          regKey: "",
          serialNo: "d593bdd26dfbea56",
          version: "1.0",
          uid: 0,
          session: getCookie("session") || "",
          sessionID: getCookie("sessionID") || "",
          userID: userData.userID || "",
          ID: 0,
          IsB2C: false,
          TID: 0,
        },
        Request: {
          BusinessType: topupInfo?.oid,
          EPin: "",
          PackageAmount: packageInfo?.packageCost,
          PackageID: packageInfo?.packageId,
          TopupUserId: userData.userID || "",
          WalletId: wallet?.id,
          couponCode: couponCode,
        },
      });

      if (res.statuscode == 1) {
        toast.success(res.msg);
        fetchPackageInformation();
        activatePackageHistory();
        setSelectedPackageId("");
        setCouponCode("");
      } else {
        toast.error(res.msg);
      }
    } catch (err) {
      console.log("error", err);
      toast.error("Failed to activate package. Please try again.");
    } finally {
      setIsActivating(false);
    }
  };

  const activatePackageHistory = async () => {
    setIsHistoryLoading(true);
    try {
      const userData = JSON.parse(getCookie("userData") || "{}");

      const res = await apiClient.post("/App/TopUpListForApp", {
        appSession: {
          appid: "ROUNDPAYAPPID13APR20191351",
          imei: "d593bdd26dfbea56",
          loginTypeID: "1",
          regKey: "",
          serialNo: "d593bdd26dfbea56",
          version: "1.0",
          uid: 0,
          session: getCookie("session") || "",
          sessionID: getCookie("sessionID") || "",
          userID: userData.userID || "",
        },
        Request: {
          FromDate: "",
          ToDate: "",
          IsRecent: true,
        },
      });

      if (res.statuscode == 1) {
        setPackageHistoryData(res.data || []);
      } else {
        setPackageHistoryData([]);
        toast.error(res.msg || "No data found");
      }
    } catch (err) {
      console.log("error", err);
      toast.error("Failed to load activation history. Please try again.");
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const historyRows = packageHistoryData || [];
  const historyPaginated = historyRows.slice(
    (historyPage - 1) * PAGE_SIZE,
    historyPage * PAGE_SIZE,
  );

  return (
    <>
      <div className="content">
        <section className="page active" id="page-activate">
          <div className="cus-flex-5487">
            <div className="price-card">
              <div className="price-head price-head-cus">
                <h3>Activate Package</h3>
              </div>
              <div className="price-rows  price-rows-cus">
                <div className="field">
                  <label>Select Package</label>
                  <select
                    value={selectedPackageId}
                    onChange={(e) => setSelectedPackageId(e.target.value)}
                  >
                    <option value="">-- Select Package --</option>
                    {packageOptions.map((pkg) => (
                      <option key={pkg.packageId} value={pkg.packageId}>
                        {pkg.packageName}
                      </option>
                    ))}
                  </select>
                </div>

                {packageInfo && (
                  <>
                    <div className="price-row">
                      <span>Package Amount</span>
                      <span className="mono">{formatAmount(packageCost)}</span>
                    </div>
                    {couponResult?.valid && (
                      <div className="price-row">
                        <span>
                          Coupon Discount ({couponResult.coupon.couponCode})
                        </span>
                        <span
                          className="mono"
                          style={{ color: "var(--green)" }}
                        >
                          -{formatAmount(couponDiscount)}
                        </span>
                      </div>
                    )}
                    <div className="price-row">
                      <span>GST (18%)</span>
                      <span className="mono">+{formatAmount(gstAmount)}</span>
                    </div>
                    <div className="price-row total">
                      <span>Total payable</span>
                      <span className="mono">{formatAmount(totalPayable)}</span>
                    </div>
                  </>
                )}

                <div className="field" style={{ marginTop: 14 }}>
                  <label>Coupon Code</label>
                  <input
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                  />
                  {couponCode.trim() && couponResult && (
                    <div
                      className="field-note"
                      style={{
                        color: couponResult.valid
                          ? "var(--green)"
                          : "var(--red)",
                      }}
                    >
                      {couponResult.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="pay-options">
                <div className="pay-opt selected">
                  <div className="radio" />
                  <div className="pay-icon">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    >
                      <rect x={2} y={6} width={20} height={14} rx="2.5" />
                      <path d="M2 10h20" />
                    </svg>
                  </div>
                  <div className="pay-opt-info">
                    <div className="t">Pay from {wallet?.name} Wallet</div>
                    <div className="s">
                      Balance: {formatAmount(wallet?.userBalance)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="activate-footer">
                <button
                  className="btn-primary"
                  style={{ width: "100%" }}
                  onClick={submitActivatePackage}
                  disabled={isActivating || !packageInfo}
                >
                  {isActivating ? "Processing..." : "Proceed to Activate"}
                </button>
              </div>
            </div>

            <div className="price-card mobile-price-card ">
              <div className="price-head">
                <h3>Coupons </h3>
              </div>
              <div className="price-rows">
                <div
                  className="table-scroll"
                  style={{ maxHeight: 360, overflowY: "auto" }}
                >
                  <table>
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        background: "var(--bg-elev)",
                        zIndex: 1,
                      }}
                    >
                      <tr>
                        <th>S.No</th>
                        <th>Coupon Code</th>
                        <th>Discount</th>
                        <th>Valid From</th>
                        <th>Valid Till</th>
                      </tr>
                    </thead>
                    <tbody>
                      {couponRows.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="table-empty">
                            No coupons available.
                          </td>
                        </tr>
                      ) : (
                        couponRows.map((coupon, idx) => (
                          <tr key={coupon.couponID}>
                            <td className="mono">{idx + 1}</td>
                            <td className="strong">
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <span className="mono">
                                  {coupon.couponCode}
                                </span>
                                <button
                                  type="button"
                                  className="btn-sm"
                                  aria-label="Copy coupon code"
                                  style={{
                                    padding: 6,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                  onClick={() =>
                                    handleCopyCoupon(coupon.couponCode)
                                  }
                                >
                                  <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    width={14}
                                    height={14}
                                  >
                                    <rect
                                      x={9}
                                      y={9}
                                      width={13}
                                      height={13}
                                      rx={2}
                                    />
                                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                            <td>{formatDiscount(coupon)}</td>
                            <td>{formatDate(coupon.validFrom)}</td>
                            <td>{formatDate(coupon.validTo)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <h3 className="section-title" style={{ marginTop: 30 }}>
            Package Activation History
          </h3>

          <div className="card">
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>TID</th>
                    <th>User Id</th>
                    <th>Sponsor Id</th>
                    <th>Topup By</th>
                    <th>Registration Date</th>
                    <th>Business Date</th>
                    <th>Package Cost</th>
                    <th>Business Type</th>
                  </tr>
                </thead>
                <tbody>
                  {isHistoryLoading ? (
                    <tr>
                      <td colSpan={9} className="table-empty">
                        Loading activation history...
                      </td>
                    </tr>
                  ) : historyRows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="table-empty">
                        No activation history found.
                      </td>
                    </tr>
                  ) : (
                    historyPaginated.map((item, idx) => (
                      <tr key={item.tid}>
                        <td className="mono">
                          {(historyPage - 1) * PAGE_SIZE + idx + 1}
                        </td>
                        <td className="mono">{item.tid}</td>
                        <td>{item.strUserId}</td>
                        <td>{item.strSponserId}</td>
                        <td>{item.strTopupBy}</td>
                        <td>{formatDate(item.regDate)}</td>
                        <td>{formatDate(item.activationDate)}</td>
                        <td className="mono">
                          {formatAmount(item.packageCost)}
                        </td>
                        <td>{item.businessType}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={historyPage}
              totalItems={historyRows.length}
              pageSize={PAGE_SIZE}
              onPageChange={setHistoryPage}
            />
          </div>
        </section>
      </div>
    </>
  );
}
